/**
 * CustomerServicePopup Component
 *
 * This component renders a customer service popup that slides in from the right.
 * It contains:
 * - Contact methods (phone, WhatsApp, email)
 * - An expandable FAQ section for advisors
 *
 * 📌 Features:
 * - Slide-in transition when opening.
 * - Click-outside-to-close functionality.
 * - Integrated `AdvisorFAQ` component for expandable advisor questions.
 *
 * 📂 Related Files:
 * - `CustomerServicePopup.module.scss` (styles)
 * - `AdvisorFAQ.tsx` (FAQ component)
 *
 *
 * @param {boolean} isOpen - Controls popup visibility.
 * @param {Function} onClose - Function to close the popup.
 * @returns {JSX.Element} The customer service popup.
 */
import React, {useEffect} from "react";
import clsx from "clsx";
import {X, Phone, Mail, MessageCircle} from "lucide-react";
import * as classnames from "@css/component/CustomerServicePopup.module.scss"
import AdvisorFAQ from '@js/component/AdvisorFAQ';
import {StaticImage} from 'gatsby-plugin-image';


interface CustomerServicePopupProps {
    isOpen: boolean;
    onClose: () => void;
}

const contactInfo = {
    phone: {
        label: "Bel ons",
        number: "085 – 01 620 80",
        link: "tel:0850162080",
    },
    whatsapp: {
        label: "App ons ",
        link: "https://wa.me/0850162080",
        text: "Open WhatsApp",
    },
    email: {
        label: "Mail ons",
        address: "info@woninglabel.nl",
        link: "mailto:info@woninglabel.nl",
        text: "info@woninglabel.nl",
    },
    iconsSize: 25,
    availability: "Ma - Vr: 09:00 - 17:00"
};

const CustomerServicePopup: React.FC<CustomerServicePopupProps> = ({
                                                                       isOpen,
                                                                       onClose
                                                                   }) => {

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("modal-open");
        } else {
            document.body.classList.remove("modal-open");
        }
    }, [isOpen]);

    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const startHour = 9;
    const endHour = 17;

    const isAvailable = currentHour >= startHour && currentHour < endHour;

    if (!isOpen) {
        return null;
    }

    return (
        <div className={clsx(classnames.overlay, {[classnames.open]: isOpen})}
             onClick={onClose}>
            <div className={clsx(classnames.popup, {[classnames.open]: isOpen})}
                 onClick={(e) => e.stopPropagation()}>
                <button className={classnames.closeButton} onClick={onClose}>
                    <X size={24} />
                </button>

                <div className={classnames.content}>
                    <div className={classnames.header}>
                        <h2>Hulp nodig?</h2>
                        <p>We helpen je graag</p>
                    </div>

                    <div className={classnames.customerServInfo}>
                        {/*<img src="/images/customerServicePerson.png"*/}
                            {/*     alt="Customer Service"*/}
                            {/*     className={classnames.avatar} />*/}
                            <StaticImage src="../../img/customerServicePerson.png" alt="Customer Service " className={classnames.avatar}/>
                            <div className={classnames.details}>
                                <h3 className={classnames.customerServInfoTitle}>Klantenservice</h3>
                                <p className={classnames.customerServInfoAvailability}>We zijn het beste bereikbaar tussen {contactInfo.availability}</p>
                            </div>
                        </div>

                    <div className={classnames.contactMethods}>
                        <a href={contactInfo.phone.link}
                           className={classnames.contactItem}>
                            <span className={classnames.contactItemIconTitle}>
                                <Phone size={contactInfo.iconsSize} />
                                {contactInfo.phone.label}
                            </span>
                            <p>{contactInfo.phone.number}</p>
                            <div className={classnames.contactItemBorder} />
                            <div className={classnames.status}>
                                <span
                                    className={isAvailable ? classnames.statusDotAvailable : classnames.statusDotUnavailable}></span>
                                <span className={classnames.availabilityText}>Beschikbaar tot 17:00</span>
                            </div>
                        </a>
                        <a href={contactInfo.whatsapp.link}
                           className={classnames.contactItem}>
                            <span className={classnames.contactItemIconTitle}>
                                <MessageCircle
                                    size={contactInfo.iconsSize} />
                                {contactInfo.whatsapp.label}
                            </span>
                            <p>{contactInfo.whatsapp.text}</p>
                            <div className={classnames.contactItemBorder} />
                            <div className={classnames.status}>
                                <span
                                    className={isAvailable ? classnames.statusDotAvailable : classnames.statusDotUnavailable}></span>
                                <span>Beschikbaar tot 17:00</span>
                            </div>
                        </a>
                        <a href={contactInfo.email.link}
                           className={classnames.contactItem}>
                            <span className={classnames.contactItemIconTitle}>
                                <Mail size={contactInfo.iconsSize} />
                                {contactInfo.email.label}
                            </span>
                            <p>{contactInfo.email.text}</p>
                            <div className={classnames.contactItemBorder} />
                            <div className={classnames.status}>
                                <span
                                    className={classnames.statusDotAvailable}></span>
                                <span>24 uur per dag</span>
                            </div>
                        </a>
                    </div>

                    <AdvisorFAQ />
                </div>
            </div>
        </div>
    );
};
export default CustomerServicePopup;

